import {
	Paperclip,
	Send,
	Trash,
	ChevronUp,
	ChevronDown,
	Lightbulb,
	Languages,
	Text,
	SpellCheck,
	ListCheck,
} from 'lucide-react'
import { ChangeEvent, ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import TextareaAutosize from 'react-textarea-autosize'
import { SendingChatEntry } from '@/models'
import useGlobalStore from '@/useGlobalStore'
import { dispatch, useBus } from '@/useBus'
import { Camera, CameraResultType } from '@capacitor/camera'
import { getTimeBasedRandomString } from '@/lib/utils.ts'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import { Capacitor } from '@capacitor/core'
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings'
import { toast } from 'sonner'

interface ChatBottomBarProps {
	sendMessage: (newMessage: SendingChatEntry) => void
	onUserMessagingInteraction: (messageLength: number, imagesCount: number) => void
}

interface ChatImageUpload {
	data: string
	name: string
}

interface SmartAction {
	label: string
	icon: any
}

const ChatBottomBar = ({ sendMessage, onUserMessagingInteraction }: ChatBottomBarProps) => {
	const { t } = useTranslation()
	const { lastActiveChatTappedTimestamp, isTrialExpired, availableAppLanguages, appLanguageCode } = useGlobalStore()
	const [message, setMessage] = useState('')
	const [images, setImages] = useState<ChatImageUpload[]>([])
	const [isSmartActionsVisible, setIsSmartActionsVisible] = useState(false)
	const inputRef = useRef<HTMLTextAreaElement>(null)
	const smartActionsRef = useRef<HTMLDivElement>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	const [queryPrefix, setQueryPrefix] = useState('')
	const [textInputHeight, setTextInputHeight] = useState(0)

	// focus suppression to avoid immediate refocus on input field after send
	const suppressFocusRef = useRef(false)
	const suppressFocusFor = (ms = 600) => {
		suppressFocusRef.current = true
		setTimeout(() => {
			suppressFocusRef.current = false
		}, ms)
	}

	const smartActions: SmartAction[] = [
		{ label: 'Explain structure', icon: Lightbulb },
		{ label: 'Summarize', icon: Text },
		{ label: 'Translate', icon: Languages },
		{ label: 'Check translation', icon: ListCheck },
		{ label: 'Check grammar', icon: SpellCheck },
	]

	useBus('chat-bar-visible', () => {
		recalculateTextInputHeight()
	})

	useBus('set-chat-message', (message: string) => {
		setMessage(message)
	})

	useBus('reset-chat', () => {
		setMessage('')
		setImages([])
		setIsSmartActionsVisible(false)
	})

	useEffect(() => {
		if (lastActiveChatTappedTimestamp !== null && !suppressFocusRef.current) {
			inputRef?.current?.focus()
		}
	}, [lastActiveChatTappedTimestamp])

	// Handle click outside to close smart actions
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node

			// Don't close if clicking inside the smart actions panel
			if (smartActionsRef.current?.contains(target)) {
				return
			}

			// Don't close if clicking inside the text input
			if (inputRef.current?.contains(target)) {
				return
			}

			// Don't close if clicking inside the container (including buttons)
			if (containerRef.current?.contains(target)) {
				// But do close if it's not the input or smart actions
				if (!inputRef.current?.contains(target) && !smartActionsRef.current?.contains(target)) {
					setIsSmartActionsVisible(false)
				}
				return
			}

			// Close if clicking outside the entire component
			if (isSmartActionsVisible) {
				setIsSmartActionsVisible(false)
			}
		}

		if (isSmartActionsVisible) {
			document.addEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isSmartActionsVisible])

	useEffect(() => {
		recalculateTextInputHeight()
	}, [])

	useEffect(() => {
		onUserMessagingInteraction(message.length, images.length)
	}, [message, images])

	const handleInputFocus = () => {
		if (suppressFocusRef.current) return
		if (message.trim() === '' && !isSmartActionsVisible && queryPrefix === '') {
			setIsSmartActionsVisible(true)
		}
	}

	const handleKeyDownSubmitChatBox = (event: KeyboardEvent<HTMLTextAreaElement>) => {
		const handleCreateNewLine = () => {
			const cursorPosition = event.currentTarget.selectionStart
			const newMessage = message.slice(0, cursorPosition) + '\n' + message.slice(cursorPosition)
			setMessage(newMessage)
			// Use requestAnimationFrame to ensure the DOM has updated
			requestAnimationFrame(() => {
				if (inputRef.current) {
					// Set the cursor position after the new line
					inputRef.current.selectionStart = cursorPosition + 1
					inputRef.current.selectionEnd = cursorPosition + 1

					// Calculate the line height (approximately)
					const lineHeight = parseInt(getComputedStyle(inputRef.current).lineHeight || '20')

					// Scroll to show the new line
					const currentLine = newMessage.substring(0, cursorPosition).split('\n').length
					inputRef.current.scrollTop = lineHeight * currentLine
				}
			})
		}
		if (event.key === 'Enter') {
			event.preventDefault()
			if (Capacitor.getPlatform() === 'web') {
				if (event.shiftKey) {
					// if it's shift + enter
					handleCreateNewLine()
				} else {
					// Handle regular ENTER behavior
					handleSendMessage()
				}
			} else {
				handleCreateNewLine()
			}
		}
	}

	const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
		setMessage(event.target.value)
		if (event.target.value.length > 0) {
			setIsSmartActionsVisible(false)
		}
	}

	const handlePaste = async (event: ClipboardEvent<HTMLTextAreaElement>) => {
		const clipboardItems = event.clipboardData?.items || []

		const imageItems = Array.from(clipboardItems).filter((item) => item.type.startsWith('image/'))

		if (imageItems.length === 0) {
			return
		}

		const remainingSlots = Math.max(0, 3 - images.length)

		if (remainingSlots === 0) {
			return
		}

		const imagesToAdd: ChatImageUpload[] = []

		for (const item of imageItems.slice(0, remainingSlots)) {
			const file = item.getAsFile()

			if (!file) {
				continue
			}

			try {
				const dataUrl = await new Promise<string>((resolve, reject) => {
					const reader = new FileReader()
					reader.onload = () => resolve(reader.result as string)
					reader.onerror = () => reject(reader.error)
					reader.readAsDataURL(file)
				})

				const extension = file.type.split('/')[1] || 'png'

				imagesToAdd.push({
					data: dataUrl,
					name: `${getTimeBasedRandomString()}.${extension}`,
				})
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
			} catch (_) {
				toast(t('Something went wrong'))
			}
		}

		if (imagesToAdd.length > 0) {
			setImages((prevImages) => [...prevImages, ...imagesToAdd])
		}
	}

	const handleSendMessage = () => {
		if (isTrialExpired) {
			dispatch('show-auth-dialog', {
				trialExpired: true,
			})
			return
		}
		if (message.trim() || (images.length > 0 && queryPrefix.length > 0)) {
			const prefix = queryPrefix.length > 0 ? `${t(queryPrefix)}:\n` : ''
			const newMessage: SendingChatEntry = {
				text: `${prefix}${message.trim()}`,
				images: images.map((x) => {
					return {
						data: x.data,
						name: x.name,
					}
				}),
			}
			sendMessage(newMessage)

			if (Capacitor.getPlatform() !== 'web') {
				suppressFocusFor(600)
				inputRef.current?.blur()
			}

			setMessage('')
			setImages([])
			setQueryPrefix('')
			setIsSmartActionsVisible(false)
		}
	}

	const handleChooseImage = async () => {
		const permission = await Camera.checkPermissions()

		if (permission.photos === 'denied' || permission.camera === 'denied') {
			// If camera permission is denied, send the user to settings
			if (Capacitor.getPlatform() === 'ios') {
				await NativeSettings.openIOS({
					option: IOSSettings.App,
				})
			} else {
				await NativeSettings.openAndroid({
					option: AndroidSettings.ApplicationDetails,
				})
			}
			return
		}

		const image = await Camera.getPhoto({
			quality: 95,
			allowEditing: false,
			resultType: CameraResultType.Uri,
		})

		if (image.webPath) {
			setImages([...images, { data: image.webPath, name: `${getTimeBasedRandomString()}.${image.format}` }])
		}
	}

	const handleSmartActionClick = (action: SmartAction) => {
		setQueryPrefix(action.label)
		setIsSmartActionsVisible(false)
		// Focus the input after a short delay to ensure the message is set
		setTimeout(() => {
			inputRef?.current?.focus()
			// Position cursor at the end
			if (inputRef.current) {
				const length = inputRef.current.value.length
				inputRef.current.setSelectionRange(length, length)
			}
		}, 100)
	}

	const toggleSmartActions = () => {
		if (!isSmartActionsVisible && queryPrefix.length > 0) {
			setQueryPrefix('')
		}
		setIsSmartActionsVisible(!isSmartActionsVisible)
	}

	const recalculateTextInputHeight = () => setTextInputHeight((inputRef?.current?.offsetHeight ?? 0) + 16)

	const totalHeightOffset = textInputHeight + (isSmartActionsVisible ? 116 : 0)

	return (
		<motion.div
			ref={containerRef}
			dir="ltr"
			className="bg-background p-4 pt-1 relative"
			initial={{ y: 100 }}
			animate={{ y: 0 }}
			transition={{ type: 'spring', damping: 20 }}>
			<AnimatePresence>
				{isSmartActionsVisible && (
					<motion.div
						dir={availableAppLanguages[appLanguageCode]?.dir}
						ref={smartActionsRef}
						className={`absolute ${availableAppLanguages[appLanguageCode]?.dir === 'ltr' ? 'ps-12 lg:ps-9 pe-16 lg:pe-12' : 'ps-[6.5rem] lg:ps-24 pe-4 lg:pe-0'} max-w-3xl mx-auto right-0 left-0 -mb-1`}
						style={{ bottom: textInputHeight + 8 }}
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: 20 }}
						transition={{ duration: 0.2 }}>
						<div className="dark:bg-secondary/95 bg-card rounded-lg shadow-lg">
							<div className="flex flex-col gap-0 py-2">
								{smartActions.map((action, idx) => {
									const IconComponent = action.icon
									return (
										<Button
											key={idx}
											variant="ghost"
											size="sm"
											onClick={() => handleSmartActionClick(action)}
											className={`flex items-center gap-1.5 px-1 ms-1 me-1 h-11 justify-start hover:bg-black/20 ${queryPrefix === action.label ? 'bg-muted' : ''}`}>
											<IconComponent className="h-3.5 w-3.5 text-primary flex-shrink-0" />
											<div className="flex flex-col items-start min-w-0">
												<span className="text-xs font-medium truncate">
													{t(action.label)}...
												</span>
											</div>
										</Button>
									)
								})}
							</div>
						</div>
					</motion.div>
				)}
			</AnimatePresence>

			{/* Images Preview */}
			<div className="absolute px-4 max-w-3xl mx-auto right-0 left-0" style={{ bottom: totalHeightOffset }}>
				<div className="flex justify-end gap-2 pe-3">
					<PhotoProvider>
						{images.map((x, idx) => (
							<div key={idx} className="w-[4.5rem] h-[4.5rem] ms-1">
								<div className="relative w-full h-full rounded-t-md shadow-md">
									<Trash
										onClick={() => setImages(images.filter((_, i) => i !== idx))}
										className="w-6 h-6 p-1 absolute bg-background hover:bg-gray-600 cursor-pointer"
									/>
									<PhotoView src={x.data}>
										<img
											src={x.data}
											alt="attached image"
											className="w-full h-full rounded-t-md object-cover"
										/>
									</PhotoView>
								</div>
							</div>
						))}
					</PhotoProvider>
					<div className="w-10"></div>
				</div>
			</div>

			<div className="max-w-3xl mx-auto relative">
				<div className="flex items-end gap-2">
					<Button
						dir={availableAppLanguages[appLanguageCode]?.dir}
						variant="secondary"
						size="sm"
						onClick={toggleSmartActions}
						className={`absolute ${availableAppLanguages[appLanguageCode]?.dir === 'ltr' ? 'start-1.5' : 'start-14'} -top-[1.125rem] h-5 min-w-6 p-0 z-10 bg-card hover:bg-secondary/50 rounded-b-none`}>
						{isSmartActionsVisible ? (
							<ChevronDown className="h-4 w-4" />
						) : (
							<ChevronUp className="h-4 w-4 mx-1" />
						)}
						{!isSmartActionsVisible && queryPrefix.length > 0 && (
							<span className="text-xs me-2">{t(queryPrefix)}:</span>
						)}
					</Button>
					<TextareaAutosize
						dir={availableAppLanguages[appLanguageCode]?.dir}
						autoFocus={true}
						ref={inputRef}
						value={message}
						onChange={handleChange}
						onKeyDown={handleKeyDownSubmitChatBox}
						onPaste={handlePaste}
						onFocus={handleInputFocus}
						onHeightChange={(height) => setTextInputHeight(height + 16)}
						placeholder={t('Ask Something')}
						className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 z-40 text-sm placeholder:text-muted-foreground !outline-none !ring-0 disabled:cursor-not-allowed disabled:opacity-50"
						minRows={1}
						maxRows={Capacitor.getPlatform() === 'web' ? 15 : 4}
					/>

					<div className="w-10 flex-shrink-0">
						<div className="relative">
							<div className="absolute -top-14 w-10 h-24 dark:bg-secondary/90 bg-card rounded-full z-[49]" />

							<div className="w-full absolute -top-12 flex justify-center z-[50]">
								<Button
									disabled={images.length >= 3}
									variant="ghost"
									size="icon"
									className="h-8 w-8 rounded-full hover:bg-primary/20"
									onClick={handleChooseImage}>
									<Paperclip className="h-4 w-4" />
								</Button>
							</div>

							<div className="relative z-[49]">
								<Button
									variant="default"
									size="icon"
									className="h-10 w-10 rounded-full"
									onClick={handleSendMessage}>
									<Send className="h-4 w-4" />
								</Button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</motion.div>
	)
}

export default ChatBottomBar
